/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { computed, action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import AnalyticsService from 'cio/services/analytics';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import urlBuilder from 'cio/utils/url-builder';
import WindowMessengerService from 'cio/services/window-messenger';

type IntegrationsExternalIntegrationArgs = {
  name: string;
  category: string;
  logoSrc: string;
  type: string;
  beta?: boolean;
  path: string;
  homepage?: string;
  public?: boolean;
  forceNotifyModal: boolean;
  publicType: string;
};

enum ModalState {
  None = 'NONE',
  Form = 'FORM',
  Email = 'EMAIL',
  Notify = 'NOTIFY',
  Thanks = 'THANKS',
}

export default class IntegrationsExternalIntegration extends Component<IntegrationsExternalIntegrationArgs> {
  @service analytics!: AnalyticsService;
  @service account!: AccountService;
  @service auth!: AuthService;
  @service windowMessenger!: WindowMessengerService;

  @tracked modal = ModalState.None;
  @tracked userEmail?: string;

  zapierWordmarkImage = new URL('cio/assets/images/integrations/zapier-wordmark.svg', import.meta.url).toString();

  get ctaHref() {
    let params: Record<string, string | undefined | null> = {
      utm_source: this.from,
      utm_campaign: 'Existing',
      utm_term: this.args.name,
      utm_content: this.args.category,
      utm_medium: this.auth?.user?.id ?? this.analytics.getAnonId(),
      salesforce_uuid: this.account?.current?.id,
    };

    return urlBuilder('https://calendly.com/customer-io-integrations/customer-io-integration-request', params);
  }

  @computed('args.public')
  get from() {
    return this.args.public ? 'Public' : 'In-App';
  }

  @computed('args.path', 'args.name')
  get model() {
    if (this.args.path === 'integrations.type.warehouse_sync') {
      return this.args.name.replace(/ /g, '_');
    }

    return undefined;
  }

  @action
  checkNotifyModal() {
    if (this.args.forceNotifyModal) {
      this.onNotifyClick();
    }
  }

  @action
  toggleFormModal() {
    if (this.modal === ModalState.None) {
      this.analytics.track('integration', 'request_started', {
        from: this.from,
        requestType: 'Existing',
        category: this.args.category,
        name: this.args.name,
        type: this.args.type,
      });
    }

    this.modal = ModalState.Form;

    if (this.args.public) {
      this.windowMessenger.send('modal-open', {});
    }
  }

  @action
  closeModal() {
    this.modal = ModalState.None;

    if (this.args.public) {
      this.windowMessenger.send('modal-close', {});
    }
  }

  @action
  submitForm() {
    this.modal = ModalState.Thanks;
  }

  @action
  onButtonClick(source: string) {
    this.analytics.track('integration', 'landing_page_button_clicked', {
      from: this.from,
      category: this.args.category,
      name: this.args.name,
      type: this.args.type,
      clickedOn: source,
    });
  }

  @action
  onEmailGathered(email: string) {
    this.userEmail = email;
    this.onNotifyClick();
  }

  @action
  onNotifyClick() {
    if (this.args.public) {
      this.windowMessenger.send('modal-open', {});
    }

    if (this.args.public && !this.userEmail) {
      this.modal = ModalState.Email;

      return;
    }

    this.analytics.track('integration', 'notify_when_available_clicked', {
      from: this.from,
      category: this.args.category,
      name: this.args.name,
      type: this.args.type,
      userEmail: this.userEmail,
    });

    this.modal = ModalState.Notify;
  }
}
